import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { Parcela, EmpBloque } from '../../types/empacadora';
import GeoMapPreview from '../../components/GeoMapPreview';
import { fromBlob } from 'geotiff';
import proj4 from 'proj4';
import { ArrowLeft, Sprout, Upload, Zap } from 'lucide-react';
import { formatNumber } from '../../utils/erpLocale';
// Asegurate de que la ruta al servicio sea la correcta
import {
  invocarMotorPython,
  invocarMotorPythonBatchArchivo,
  type MotorPythonResponse,
} from './deteccionService';

interface DetectParams {
  minArea: number;
  maxArea: number;
  hMin: number;
  hMax: number;
  sMin: number;
  vMin: number;
  ndreMin: number;
}

interface PlantDetectionResult {
  lat: number;
  lng: number;
  x?: number;
  y?: number;
}

interface ParcelaStats {
  bloques: number;
  areaTotal: number;
  areaSembrada: number;
}

interface DetectionArtifacts {
  jsonPath?: string;
  imagePath?: string;
  reportPath?: string;
  jsonUrl?: string;
  imageUrl?: string;
  reportUrl?: string;
}

interface BatchBlockResult {
  bloqueId: string;
  bloqueNum: number;
  areaHa: number;
  conteo: number;
  densidad: number;
  kpi: number;
  jsonUrl?: string;
  reportUrl?: string;
  batchReportUrl?: string;
  batchJsonUrl?: string;
  error?: string;
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * (((b - r) / delta) + 2);
    else h = 60 * (((r - g) / delta) + 4);
    if (h < 0) h += 360;
  }

  const s = max === 0 ? 0 : delta / max;
  const v = max;

  return [h, s, v];
}

function createGreenMask(
  rasters: Uint8Array,
  width: number,
  height: number,
  hMin: number,
  hMax: number,
  sMin: number,
  vMin: number,
  samplesPerPixel: number
): boolean[] {
  const mask = new Array<boolean>(width * height).fill(false);
  const stride = Math.max(3, samplesPerPixel);

  for (let i = 0; i < width * height; i++) {
    const base = i * stride;
    const r = rasters[base];
    const g = rasters[base + 1];
    const b = rasters[base + 2];

    const [h, s, v] = rgbToHsv(r, g, b);
    if (h >= hMin && h <= hMax && s >= sMin && v >= vMin) {
      mask[i] = true;
    }
  }

  return mask;
}

function createNdreMask(ndre: Float32Array, width: number, height: number, ndreMin: number): boolean[] {
  const mask = new Array<boolean>(width * height).fill(false);
  for (let i = 0; i < width * height; i++) {
    const value = ndre[i];
    if (Number.isFinite(value) && value >= ndreMin) {
      mask[i] = true;
    }
  }
  return mask;
}

function erode(mask: boolean[], width: number, height: number): boolean[] {
  const result = new Array<boolean>(width * height).fill(false);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (
        mask[idx] &&
        mask[idx - 1] &&
        mask[idx + 1] &&
        mask[(y - 1) * width + x] &&
        mask[(y + 1) * width + x] &&
        mask[(y - 1) * width + x - 1] &&
        mask[(y - 1) * width + x + 1] &&
        mask[(y + 1) * width + x - 1] &&
        mask[(y + 1) * width + x + 1]
      ) {
        result[idx] = true;
      }
    }
  }
  return result;
}

function dilate(mask: boolean[], width: number, height: number): boolean[] {
  const result = new Array<boolean>(width * height).fill(false);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (mask[y * width + x]) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const ny = y + dy;
            const nx = x + dx;
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              result[ny * width + nx] = true;
            }
          }
        }
      }
    }
  }
  return result;
}

function labelComponents(
  mask: boolean[],
  width: number,
  height: number
): Map<number, number[]> {
  const labels = new Int32Array(width * height);
  let nextLabel = 1;
  const components = new Map<number, number[]>();

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (!mask[idx] || labels[idx] !== 0) continue;

      const queue: number[] = [idx];
      labels[idx] = nextLabel;
      const pixels: number[] = [idx];

      while (queue.length > 0) {
        const cur = queue.shift()!;
        const cy = Math.floor(cur / width);
        const cx = cur % width;

        const neighbors = [
          [cy - 1, cx],
          [cy + 1, cx],
          [cy, cx - 1],
          [cy, cx + 1],
        ];

        for (const [ny, nx] of neighbors) {
          if (ny < 0 || ny >= height || nx < 0 || nx >= width) continue;
          const nidx = ny * width + nx;
          if (!mask[nidx] || labels[nidx] !== 0) continue;
          labels[nidx] = nextLabel;
          queue.push(nidx);
          pixels.push(nidx);
        }
      }

      components.set(nextLabel, pixels);
      nextLabel++;
    }
  }

  return components;
}

function getCentroid(pixels: number[], width: number): [number, number] {
  let sumX = 0,
    sumY = 0;
  for (const idx of pixels) {
    sumX += idx % width;
    sumY += Math.floor(idx / width);
  }
  return [sumX / pixels.length, sumY / pixels.length];
}

function pointInPolygon(lon: number, lat: number, polygon: number[][]): boolean {
  let isInside = false;
  const x = lon,
    y = lat;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [x1, y1] = polygon[i];
    const [x2, y2] = polygon[j];

    const xi = x1,
      yi = y1;
    const xj = x2,
      yj = y2;

    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      isInside = !isInside;
    }
  }

  return isInside;
}

export function ConteoPlantasV2Page() {
  const empresaId = useEmpresaId();
  const [activeRoute, setActiveRoute] = useState<'select' | 'detect'>('select');

  // Data
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [bloques, setBloques] = useState<EmpBloque[]>([]);
  const [parcelaStatsMap, setParcelaStatsMap] = useState<Map<string, ParcelaStats>>(new Map());

  // Selection
  const [selectedParcelaId, setSelectedParcelaId] = useState<string>('');
  const [selectedBloqueId, setSelectedBloqueId] = useState<string>('');

  // GeoTIFF
  const [geotiffLayer, setGeotiffLayer] = useState<{
    url: string;
    bounds?: [[number, number], [number, number]];
    fullRasters?: Uint8Array;
    ndreRasters?: Float32Array;
    origW?: number;
    origH?: number;
    displayScale?: number;
    samplesPerPixel?: number;
    swLng?: number;
    swLat?: number;
    neLng?: number;
    neLat?: number;
    georeferenced?: boolean;
    mode: 'rgb' | 'ms';
    sourceLabel?: string;
  } | null>(null);
  const [geotiffLoadingError, setGeotiffLoadingError] = useState('');
  const [msHint, setMsHint] = useState('');
  const geotiffInputRef = useRef<HTMLInputElement>(null);
  const geotiffInputDetectRef = useRef<HTMLInputElement>(null);
  const msInputRef = useRef<HTMLInputElement>(null);
  const msReInputRef = useRef<HTMLInputElement>(null);
  const msNirInputRef = useRef<HTMLInputElement>(null);
  const selectedRasterFileRef = useRef<File | null>(null);
  const [hasRasterFile, setHasRasterFile] = useState(false);
  const [previewTab, setPreviewTab] = useState<'image' | 'map'>('map');
  const [detectMode, setDetectMode] = useState<'rgb' | 'ms'>('rgb');
  const [pendingMsFiles, setPendingMsFiles] = useState<{ re?: File; nir?: File }>({});

  // Detection
  const [detectParams, setDetectParams] = useState<DetectParams>({
    minArea: 20,
    maxArea: 800,
    hMin: 25,
    hMax: 90,
    sMin: 0.4,
    vMin: 0.4,
    ndreMin: 0.08,
  });
  const [plantas, setPlantas] = useState<PlantDetectionResult[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState('');
  const [artifacts, setArtifacts] = useState<DetectionArtifacts | null>(null);
  const [detectedAreaHa, setDetectedAreaHa] = useState<number | null>(null);
  const [geometryValidated, setGeometryValidated] = useState(false);
  const [selectedBatchBloqueIds, setSelectedBatchBloqueIds] = useState<string[]>([]);
  const [batchDetecting, setBatchDetecting] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchBlockResult[]>([]);
  const [batchReportUrl, setBatchReportUrl] = useState<string>('');
  const [batchGeojsonUrl, setBatchGeojsonUrl] = useState<string>('');

  // Saving
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load parcelas on mount
  useEffect(() => {
    const load = async () => {
      const [parcelasRes, bloquesRes] = await Promise.all([
        supabase
          .from('emp_parcelas')
          .select('*')
          .eq('empresa_id', empresaId)
          .eq('activo', true)
          .order('nombre'),
        supabase
          .from('emp_bloques')
          .select('parcela_id, tipo, area_ha')
          .eq('empresa_id', empresaId)
          .eq('activo', true),
      ]);

      if (parcelasRes.data) setParcelas(parcelasRes.data);

      if (bloquesRes.data) {
        const stats = new Map<string, ParcelaStats>();
        (bloquesRes.data as Pick<EmpBloque, 'parcela_id' | 'tipo' | 'area_ha'>[]).forEach((bloque) => {
          const parcelaId = String(bloque.parcela_id || '');
          if (!parcelaId) return;
          const prev = stats.get(parcelaId) || { bloques: 0, areaTotal: 0, areaSembrada: 0 };
          const area = typeof bloque.area_ha === 'number' ? bloque.area_ha : 0;
          prev.bloques += 1;
          prev.areaTotal += area;
          if (bloque.tipo === 'siembra') prev.areaSembrada += area;
          stats.set(parcelaId, prev);
        });
        setParcelaStatsMap(stats);
      } else {
        setParcelaStatsMap(new Map());
      }
    };
    load();
  }, [empresaId]);

  // Load bloques when parcela changes
  useEffect(() => {
    if (!selectedParcelaId) {
      setBloques([]);
      setSelectedBatchBloqueIds([]);
      return;
    }
    setBloques([]);
    setSelectedBatchBloqueIds([]);
    const load = async () => {
      const { data } = await supabase
        .from('emp_bloques')
        .select('*')
        .eq('parcela_id', selectedParcelaId)
        .eq('tipo', 'siembra')
        .eq('activo', true)
        .order('num');
      if (data) setBloques(data);
    };
    load();
  }, [selectedParcelaId]);

  useEffect(() => {
    if (activeRoute !== 'detect') return;
    if (bloques.length === 0) return;
    if (selectedBatchBloqueIds.length > 0) return;
    setSelectedBatchBloqueIds(bloques.map((b) => b.id));
  }, [activeRoute, bloques, selectedBatchBloqueIds.length]);

  const selectedParcela = parcelas.find((p) => p.id === selectedParcelaId);
  const selectedParcelaStats = selectedParcelaId
    ? parcelaStatsMap.get(selectedParcelaId) || { bloques: 0, areaTotal: 0, areaSembrada: 0 }
    : { bloques: 0, areaTotal: 0, areaSembrada: 0 };
  const selectedBloque = bloques.find((b) => b.id === selectedBloqueId);
  const selectedBatchBloques = bloques.filter((b) => selectedBatchBloqueIds.includes(b.id));
  const batchSummary = useMemo(() => {
    const okRows = batchResults.filter((r) => !r.error);
    const totalConteo = okRows.reduce((acc, row) => acc + (Number(row.conteo) || 0), 0);
    const totalAreaHa = okRows.reduce((acc, row) => acc + (Number(row.areaHa) || 0), 0);
    return { totalConteo, totalAreaHa, okCount: okRows.length, totalRows: batchResults.length };
  }, [batchResults]);
  const displayedPlantCount = batchSummary.totalConteo > 0 ? batchSummary.totalConteo : plantas.length;
  const displayedAreaHa =
    detectedAreaHa ||
    (batchSummary.totalAreaHa > 0 ? batchSummary.totalAreaHa : (selectedBloque?.area_ha || 0));
  const displayedDensity = displayedAreaHa > 0 ? displayedPlantCount / displayedAreaHa : 0;
  const formatHa = (value: number) => (value > 0 ? `${value.toFixed(2)} ha` : '-');
  const classifyMsBand = useCallback((name: string): 're' | 'nir' | null => {
    const n = name.toUpperCase();
    const compact = n.replace(/[^A-Z0-9]/g, '');
    const tokens = n.split(/[^A-Z0-9]+/).filter(Boolean);
    if (compact.includes('MSNIR') || tokens.includes('NIR') || tokens.includes('MSNIR')) return 'nir';
    if (
      compact.includes('MSRE') ||
      tokens.includes('RE') ||
      tokens.includes('MSRE') ||
      (tokens.includes('MS') && tokens.includes('RE'))
    ) {
      return 're';
    }
    return null;
  }, []);

  const resolveGeoBounds = useCallback((image: any): {
    bounds?: [[number, number], [number, number]];
    swLng: number;
    swLat: number;
    neLng: number;
    neLat: number;
    georeferenced: boolean;
  } => {
    try {
      const bbox = image.getBoundingBox();
      const geoKeys = image.getGeoKeys() as any;
      const projCSCode: number = geoKeys.ProjectedCSTypeGeoKey || 0;
      const geogCSCode: number = geoKeys.GeographicTypeGeoKey || 0;
      const epsg = projCSCode || geogCSCode;

      let swLng: number;
      let swLat: number;
      let neLng: number;
      let neLat: number;
      let georeferenced = false;

      if (epsg === 4326) {
        [swLng, swLat, neLng, neLat] = bbox;
        georeferenced = true;
      } else if (epsg >= 32601 && epsg <= 32660) {
        const fromProj = `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`;
        const sw = proj4(fromProj, 'WGS84', [bbox[0], bbox[1]]);
        const ne = proj4(fromProj, 'WGS84', [bbox[2], bbox[3]]);
        [swLng, swLat] = sw;
        [neLng, neLat] = ne;
        georeferenced = true;
      } else if (epsg >= 32701 && epsg <= 32760) {
        const fromProj = `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`;
        const sw = proj4(fromProj, 'WGS84', [bbox[0], bbox[1]]);
        const ne = proj4(fromProj, 'WGS84', [bbox[2], bbox[3]]);
        [swLng, swLat] = sw;
        [neLng, neLat] = ne;
        georeferenced = true;
      } else if (epsg >= 25828 && epsg <= 25838) {
        const fromProj = `+proj=utm +zone=${epsg - 25800} +ellps=GRS80 +units=m +no_defs`;
        const sw = proj4(fromProj, 'WGS84', [bbox[0], bbox[1]]);
        const ne = proj4(fromProj, 'WGS84', [bbox[2], bbox[3]]);
        [swLng, swLat] = sw;
        [neLng, neLat] = ne;
        georeferenced = true;
      } else {
        const width = Math.max(1, Number(image.getWidth()) || 1);
        const height = Math.max(1, Number(image.getHeight()) || 1);
        swLng = 0;
        swLat = 0;
        neLng = width;
        neLat = height;
      }

      return {
        bounds: georeferenced ? [[swLat, swLng], [neLat, neLng]] : undefined,
        swLng,
        swLat,
        neLng,
        neLat,
        georeferenced,
      };
    } catch {
      const width = Math.max(1, Number(image.getWidth()) || 1);
      const height = Math.max(1, Number(image.getHeight()) || 1);
      return {
        bounds: undefined,
        swLng: 0,
        swLat: 0,
        neLng: width,
        neLat: height,
        georeferenced: false,
      };
    }
  }, []);

  const processGeoTiff = useCallback(
    async (file: File): Promise<boolean> => {
      setGeotiffLoadingError('');
      setMsHint('');
      try {
        const tiff = await fromBlob(file);
        const image = await tiff.getImage();
        const geo = resolveGeoBounds(image);

        const origW = image.getWidth();
        const origH = image.getHeight();
        const samplesPerPixel = Math.max(3, Number(image.getSamplesPerPixel()) || 3);

        const displayMaxPx = 4000;
        const displayScale = Math.min(1, displayMaxPx / Math.max(origW, origH));
        const dispW = Math.round(origW * displayScale);
        const dispH = Math.round(origH * displayScale);

        const rasters = (await image.readRasters({
          interleave: true,
          width: dispW,
          height: dispH,
        })) as unknown as Uint8Array;

        const canvas = document.createElement('canvas');
        canvas.width = dispW;
        canvas.height = dispH;
        const ctx = canvas.getContext('2d')!;
        const imgData = ctx.createImageData(dispW, dispH);

        for (let i = 0; i < dispW * dispH; i++) {
          const base = i * samplesPerPixel;
          const r = rasters[base] ?? 0;
          const g = rasters[base + 1] ?? r;
          const b = rasters[base + 2] ?? r;
          const a =
            samplesPerPixel >= 4
              ? (rasters[base + 3] ?? 255)
              : r === 0 && g === 0 && b === 0
                ? 0
                : 255;
          imgData.data[i * 4] = r;
          imgData.data[i * 4 + 1] = g;
          imgData.data[i * 4 + 2] = b;
          imgData.data[i * 4 + 3] = a;
        }
        ctx.putImageData(imgData, 0, 0);

        const fullRasters = (await image.readRasters({
          interleave: true,
          width: origW,
          height: origH,
        })) as unknown as Uint8Array;

        return new Promise((resolve) => {
          canvas.toBlob((blob) => {
            if (!blob) {
              setGeotiffLoadingError('Error al renderizar canvas');
              resolve(false);
              return;
            }
            selectedRasterFileRef.current = file;
            setHasRasterFile(true);
            setGeometryValidated(false);
            setPreviewTab('map');
            setDetectMode('rgb');
            setPendingMsFiles({});
            setMsHint('');
            setGeotiffLayer({
              url: URL.createObjectURL(blob),
              bounds: geo.bounds,
              fullRasters,
              origW,
              origH,
              displayScale,
              samplesPerPixel,
              swLng: geo.swLng,
              swLat: geo.swLat,
              neLng: geo.neLng,
              neLat: geo.neLat,
              georeferenced: geo.georeferenced,
              mode: 'rgb',
              sourceLabel: file.name,
            });
            resolve(true);
          }, 'image/png');
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        setGeotiffLoadingError(`Error: ${msg}`);
        return false;
      }
    },
    [resolveGeoBounds]
  );

  const processRasterImage = useCallback(async (file: File): Promise<boolean> => {
    setGeotiffLoadingError('');
    setMsHint('');
    try {
      const bitmap = await createImageBitmap(file);
      const width = bitmap.width;
      const height = bitmap.height;
      if (!width || !height) throw new Error('Imagen invalida');

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo crear canvas');
      ctx.drawImage(bitmap, 0, 0);

      const rgba = ctx.getImageData(0, 0, width, height).data;
      const fullRasters = new Uint8Array(width * height * 3);
      for (let i = 0; i < width * height; i++) {
        const src = i * 4;
        const dst = i * 3;
        fullRasters[dst] = rgba[src];
        fullRasters[dst + 1] = rgba[src + 1];
        fullRasters[dst + 2] = rgba[src + 2];
      }

      setDetectMode('rgb');
      selectedRasterFileRef.current = file;
      setHasRasterFile(true);
      setGeometryValidated(false);
      setPreviewTab('map');
      setPendingMsFiles({});
      setMsHint('');
      setGeotiffLayer({
        url: URL.createObjectURL(file),
        fullRasters,
        origW: width,
        origH: height,
        displayScale: 1,
        samplesPerPixel: 3,
        swLng: 0,
        swLat: 0,
        neLng: width,
        neLat: height,
        georeferenced: false,
        mode: 'rgb',
        sourceLabel: file.name,
      });
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setGeotiffLoadingError(`Error imagen: ${msg}`);
      return false;
    }
  }, []);

  const processMultispectralSet = useCallback(
    async (files: File[]): Promise<boolean> => {
      setGeotiffLoadingError('');
      setMsHint('');
      try {
        const tifFiles = files.filter((f) => /\.(tif|tiff)$/i.test(f.name));
        const reFile = tifFiles.find((f) => classifyMsBand(f.name) === 're');
        const nirFile = tifFiles.find((f) => classifyMsBand(f.name) === 'nir');

        if (!reFile || !nirFile) {
          const selected = tifFiles.map((f) => f.name).join(', ') || '(sin TIFF)';
          const missing = [
            !reFile ? 'MS_RE' : null,
            !nirFile ? 'MS_NIR' : null,
          ].filter(Boolean).join(' + ');
          throw new Error(`Falta ${missing}. Archivos seleccionados: ${selected}`);
        }

        const [reTiff, nirTiff] = await Promise.all([fromBlob(reFile), fromBlob(nirFile)]);
        const [reImage, nirImage] = await Promise.all([reTiff.getImage(), nirTiff.getImage()]);
        const width = reImage.getWidth();
        const height = reImage.getHeight();
        if (width !== nirImage.getWidth() || height !== nirImage.getHeight()) {
          throw new Error('RE y NIR deben tener la misma resolucion');
        }

        const [reRaw, nirRaw] = await Promise.all([
          reImage.readRasters({ interleave: true }),
          nirImage.readRasters({ interleave: true }),
        ]);

        const reBand = reRaw as unknown as ArrayLike<number>;
        const nirBand = nirRaw as unknown as ArrayLike<number>;
        if (reBand.length < width * height || nirBand.length < width * height) {
          throw new Error('Bandas multiespectrales incompletas');
        }

        const ndre = new Float32Array(width * height);
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < width * height; i++) {
          const re = Number(reBand[i]);
          const nir = Number(nirBand[i]);
          const denom = nir + re;
          const value = denom > 0 ? (nir - re) / denom : -1;
          ndre[i] = value;
          if (Number.isFinite(value)) {
            if (value < min) min = value;
            if (value > max) max = value;
          }
        }
        if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
          min = -1;
          max = 1;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No se pudo crear preview NDRE');
        const imgData = ctx.createImageData(width, height);
        const range = Math.max(0.0001, max - min);
        for (let i = 0; i < width * height; i++) {
          const t = Math.min(1, Math.max(0, (ndre[i] - min) / range));
          const r = Math.round(25 + (1 - t) * 165);
          const g = Math.round(20 + t * 210);
          const b = Math.round(40 + (1 - t) * 55);
          imgData.data[i * 4] = r;
          imgData.data[i * 4 + 1] = g;
          imgData.data[i * 4 + 2] = b;
          imgData.data[i * 4 + 3] = 255;
        }
        ctx.putImageData(imgData, 0, 0);

        const geo = resolveGeoBounds(reImage);
        const previewUrl = await new Promise<string>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('No se pudo construir preview NDRE'));
              return;
            }
            resolve(URL.createObjectURL(blob));
          }, 'image/png');
        });

        setDetectMode('ms');
        selectedRasterFileRef.current = null;
        setHasRasterFile(false);
        setGeometryValidated(false);
        setGeotiffLayer({
          url: previewUrl,
          ndreRasters: ndre,
          origW: width,
          origH: height,
          displayScale: 1,
          samplesPerPixel: 1,
          bounds: geo.bounds,
          swLng: geo.swLng,
          swLat: geo.swLat,
          neLng: geo.neLng,
          neLat: geo.neLat,
          georeferenced: geo.georeferenced,
          mode: 'ms',
          sourceLabel: `${reFile.name} + ${nirFile.name}`,
        });
        setMsHint('Set multiespectral completo: RE + NIR cargados');
        return true;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        setGeotiffLoadingError(`Error multiespectral: ${msg}`);
        return false;
      }
    },
    [resolveGeoBounds, classifyMsBand]
  );

  const getCandidateIds = useCallback(() => {
    if (!selectedParcela) return [];
    return [
      String((selectedParcela as any).codigo || '').trim(),
      String(selectedParcela.nombre || '').trim().replace(/\s+/g, ''),
      String(selectedParcela.nombre || '').trim(),
    ]
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index);
  }, [selectedParcela]);

  const detectarConCandidatos = useCallback(
    async (geojson: any): Promise<MotorPythonResponse> => {
      if (!selectedRasterFileRef.current) {
        throw new Error('Debes subir un archivo RGB/GeoTIFF antes de detectar.');
      }
      const candidateIds = getCandidateIds();
      if (candidateIds.length === 0) {
        throw new Error('No se pudo determinar el lote_id para enviar al motor.');
      }
      let lastError: unknown = null;
      for (const loteId of candidateIds) {
        try {
          return await invocarMotorPython(
            loteId,
            detectParams,
            geojson,
            selectedRasterFileRef.current
          );
        } catch (error) {
          lastError = error;
        }
      }
      const msg = lastError instanceof Error ? lastError.message : String(lastError || 'Error desconocido');
      throw new Error(`No se pudo detectar con ningun lote_id candidato (${candidateIds.join(', ')}). ${msg}`);
    },
    [detectParams, getCandidateIds]
  );

  const handleDetectBatch = useCallback(async () => {
    if (!selectedParcela) {
      setDetectError('Selecciona una parcela para procesar bloques.');
      return;
    }
    const targetBlocks = bloques.filter((b) => selectedBatchBloqueIds.includes(b.id));
    if (targetBlocks.length === 0) {
      setDetectError('Marca al menos un bloque para generar el reporte.');
      return;
    }
    if (!selectedRasterFileRef.current) {
      setDetectError('Sube primero el archivo RGB/GeoTIFF para procesar bloques.');
      return;
    }

    setBatchDetecting(true);
    setDetectError('');
    setBatchResults([]);
    setBatchReportUrl('');
    setBatchGeojsonUrl('');
    try {
      const candidateIds = getCandidateIds();
      if (candidateIds.length === 0) {
        throw new Error('No se pudo determinar el lote_id para enviar al motor.');
      }

      let nextResults: BatchBlockResult[] = [];
      if (selectedRasterFileRef.current) {
        try {
          const res = await invocarMotorPythonBatchArchivo(
            candidateIds[0],
            detectParams,
            targetBlocks.map((b) => ({
              bloque_id: String(b.id),
              bloque_num: Number(b.num || 0),
              area_ha: typeof b.area_ha === 'number' ? b.area_ha : 0,
              geojson: b.geojson,
            })),
            selectedRasterFileRef.current
          );
          if (res.status !== 'success') {
            throw new Error(res.mensaje || 'Motor batch sin success');
          }
          nextResults = (res.bloques || []).map((row) => ({
            bloqueId: String(row.bloque_id || ''),
            bloqueNum: Number(row.bloque_num || 0),
            areaHa: Number(row.area_ha || 0),
            conteo: Number(row.conteo || 0),
            densidad: Number(row.densidad || 0),
            kpi: Number(row.kpi || 0),
            error: row.error || undefined,
          }));
          setBatchReportUrl(res.resultado_reporte_url || '');
          setBatchGeojsonUrl(res.resultado_json_url || '');
        } catch (batchErr) {
          const msg = batchErr instanceof Error ? batchErr.message : String(batchErr);
          const endpointMissing = /404|Not Found|detectar-bloques-archivo/i.test(msg);
          if (!endpointMissing) throw batchErr;
          // Fallback para motores PY antiguos sin endpoint batch.
          for (const block of targetBlocks) {
            try {
              const res = await detectarConCandidatos(block.geojson);
              const conteo = Number(res.conteo || (res.puntos || []).length || 0);
              const areaHa = typeof block.area_ha === 'number' ? block.area_ha : 0;
              const densidad = areaHa > 0 ? conteo / areaHa : 0;
              const kpi = (densidad / 67000) * 100;
              nextResults.push({
                bloqueId: block.id,
                bloqueNum: Number(block.num || 0),
                areaHa,
                conteo,
                densidad,
                kpi,
                jsonUrl: res.resultado_json_url,
                reportUrl: res.resultado_reporte_url,
              });
            } catch (error) {
              const emsg = error instanceof Error ? error.message : String(error);
              nextResults.push({
                bloqueId: block.id,
                bloqueNum: Number(block.num || 0),
                areaHa: typeof block.area_ha === 'number' ? block.area_ha : 0,
                conteo: 0,
                densidad: 0,
                kpi: 0,
                error: emsg,
              });
            }
          }
          setSaveMessage('Motor PY sin endpoint batch: se uso modo individual por bloque.');
          setTimeout(() => setSaveMessage(''), 5000);
        }
      }

      nextResults.sort((a, b) => a.bloqueNum - b.bloqueNum);
      setBatchResults(nextResults);
      const okCount = nextResults.filter((r) => !r.error).length;
      const failCount = nextResults.length - okCount;
      setSaveMessage(`Reporte por bloques: ${okCount} ok, ${failCount} con error.`);
      setTimeout(() => setSaveMessage(''), 4500);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setDetectError(`Error en reporte por bloques: ${msg}`);
    } finally {
      setBatchDetecting(false);
    }
  }, [selectedParcela, bloques, selectedBatchBloqueIds, detectarConCandidatos, getCandidateIds, detectParams]);


  const handleSave = useCallback(async () => {
    if (!selectedBloqueId) return;
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const payload = {
        plant_count: plantas.length,
        updated_at: nowIso,
        conteo_detectado_at: nowIso,
        conteo_modo: geotiffLayer?.mode || detectMode,
        conteo_min_area: detectParams.minArea,
        conteo_max_area: detectParams.maxArea,
        conteo_resultado_json_path: artifacts?.jsonPath || null,
        conteo_resultado_imagen_path: artifacts?.imagePath || null,
        conteo_resultado_reporte_path: artifacts?.reportPath || null,
        conteo_resultado_json_url: artifacts?.jsonUrl || null,
        conteo_resultado_imagen_url: artifacts?.imageUrl || null,
        conteo_resultado_reporte_url: artifacts?.reportUrl || null,
        conteo_resultado_payload: {
          conteo: plantas.length,
          mode: geotiffLayer?.mode || detectMode,
          params: {
            min_area: detectParams.minArea,
            max_area: detectParams.maxArea,
            ndre_min: detectParams.ndreMin,
          },
          artefactos: artifacts || null,
          saved_at: nowIso,
        },
      };

      const { error } = await supabase
        .from('emp_bloques')
        .update(payload)
        .eq('id', selectedBloqueId)
        .eq('empresa_id', empresaId);
      if (!error) {
        setSaveMessage(`Guardado: ${plantas.length} plantas + artefactos PY`);
        setTimeout(() => setSaveMessage(''), 3500);
        return;
      }

      const missingColumn =
        /column .* does not exist|PGRST204|42703/i.test(String(error.message || ''));
      if (missingColumn) {
        const { error: fallbackError } = await supabase
          .from('emp_bloques')
          .update({
            plant_count: plantas.length,
            updated_at: nowIso,
          })
          .eq('id', selectedBloqueId)
          .eq('empresa_id', empresaId);
        if (fallbackError) throw fallbackError;
        setSaveMessage('Guardado parcial: aplica migracion SQL para guardar JSON/imagen/reporte.');
        setTimeout(() => setSaveMessage(''), 4500);
        return;
      }

      throw error;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setSaveMessage(`Error al guardar: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [selectedBloqueId, plantas, empresaId, artifacts, detectParams, detectMode, geotiffLayer]);

  if (activeRoute === 'select') {
    return (
      <div className="w-full p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sprout size={24} /> Conteo de Plantas
            </h1>
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
              Detecta plantas de pina en GeoTIFF/JPG y set multiespectral (V2)
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
            Parcela
          </label>

          {parcelas.length === 0 && (
            <div className="rounded-lg border px-3 py-4 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)', color: 'var(--ink-faint)' }}>
              No hay parcelas activas
            </div>
          )}

          {parcelas.map((p) => {
            const stats = parcelaStatsMap.get(p.id) || { bloques: 0, areaTotal: 0, areaSembrada: 0 };
            const selected = selectedParcelaId === p.id;
            const expanded = false;
            const siembraPct = stats.areaTotal > 0 ? (stats.areaSembrada / stats.areaTotal) * 100 : 0;

            return (
              <div
                key={p.id}
                className="overflow-hidden rounded-xl border"
                style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (stats.bloques <= 0) return;
                    setSelectedParcelaId(p.id);
                    setSelectedBloqueId('');
                    setSelectedBatchBloqueIds([]);
                    setPreviewTab('map');
                    setActiveRoute('detect');
                  }}
                  disabled={stats.bloques <= 0}
                  className="w-full px-4 py-3 text-left transition"
                  style={{
                    background: selected ? 'var(--surface-overlay)' : 'var(--surface-raised)',
                    color: 'var(--ink)',
                    boxShadow: selected ? 'inset 0 0 0 1px var(--emp-accent)' : 'none',
                    opacity: stats.bloques <= 0 ? 0.7 : 1,
                    cursor: stats.bloques <= 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs" style={{ color: 'var(--emp-accent)' }}>{p.codigo || '-'}</span>
                        <span className="text-base font-semibold truncate" style={{ color: 'var(--ink)' }}>{p.nombre}</span>
                      </div>
                      {p.ubicacion && (
                        <p className="mt-0.5 text-xs truncate" style={{ color: 'var(--ink-faint)' }}>
                          {p.ubicacion}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap gap-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
                        <span>{stats.bloques} bloque{stats.bloques === 1 ? '' : 's'}</span>
                        <span>{formatHa(stats.areaTotal)} total</span>
                        <span style={{ color: 'var(--emp-accent)' }}>{formatHa(stats.areaSembrada)} sembrada</span>
                      </div>
                    </div>
                    <div className="shrink-0" style={{ color: 'var(--ink-faint)' }}>
                      {stats.bloques > 0 ? <span className="text-[11px]">Abrir</span> : <span className="text-[11px]">Sin bloques</span>}
                    </div>
                  </div>

                  {stats.areaTotal > 0 && (
                    <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--line)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{ background: 'var(--emp-accent)', width: `${Math.min(100, Math.max(0, siembraPct))}%` }}
                      />
                    </div>
                  )}
                </button>

                {false && expanded && (
                  <div className="border-t px-4 py-3" style={{ borderColor: 'var(--line-dim)', background: 'var(--surface-overlay)' }}>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
                      Bloques de siembra
                    </p>
                    <div className="grid gap-2 md:grid-cols-2">
                      {bloques.length === 0 ? (
                        <div className="rounded-lg border px-3 py-2 text-xs md:col-span-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)', color: 'var(--ink-faint)' }}>
                          No hay bloques de siembra activos
                        </div>
                      ) : (
                        bloques.map((b) => {
                          const bloqueSelected = selectedBloqueId === b.id;
                          const includedInBatch = selectedBatchBloqueIds.includes(b.id);
                          return (
                            <div
                              key={b.id}
                              style={{
                                borderColor: 'var(--line)',
                                background: 'var(--surface-raised)',
                              }}
                              className="rounded-lg border px-3 py-2 transition"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <button
                                  type="button"
                                  onClick={() => setSelectedBloqueId(b.id)}
                                  className="flex-1 text-left rounded px-2 py-1"
                                  style={{
                                    background: bloqueSelected ? 'var(--surface-overlay)' : 'transparent',
                                    boxShadow: bloqueSelected ? 'inset 0 0 0 1px var(--emp-accent)' : 'none',
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Bloque {b.num}</span>
                                    <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                                      {typeof b.area_ha === 'number' ? `${b.area_ha.toFixed(2)} ha` : '-'}
                                    </span>
                                  </div>
                                </button>
                                <label className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                                  <input
                                    type="checkbox"
                                    checked={includedInBatch}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setSelectedBatchBloqueIds((prev) => {
                                        if (checked) return prev.includes(b.id) ? prev : [...prev, b.id];
                                        return prev.filter((id) => id !== b.id);
                                      });
                                    }}
                                  />
                                  Incluir
                                </label>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {bloques.length > 0 && (
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                          Marcados para reporte: {selectedBatchBloqueIds.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-[11px] px-2 py-1 rounded border"
                            style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}
                            onClick={() => setSelectedBatchBloqueIds(bloques.map((b) => b.id))}
                          >
                            Seleccionar todos
                          </button>
                          <button
                            type="button"
                            className="text-[11px] px-2 py-1 rounded border"
                            style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}
                            onClick={() => setSelectedBatchBloqueIds([])}
                          >
                            Limpiar
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedBatchBloqueIds.length > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="text-[11px] px-2.5 py-1.5 rounded border font-semibold disabled:opacity-50"
                          style={{ borderColor: 'var(--emp-accent)', color: 'var(--emp-accent)' }}
                          onClick={() => {
                            if (!selectedBloqueId && selectedBatchBloqueIds.length > 0) {
                              setSelectedBloqueId(selectedBatchBloqueIds[0]);
                            }
                            setActiveRoute('detect');
                          }}
                        >
                          Abrir lote ({selectedBatchBloqueIds.length} bloques)
                        </button>
                        <button
                          type="button"
                          className="text-[11px] px-2.5 py-1.5 rounded border"
                          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}
                          onClick={() => setSelectedBatchBloqueIds([])}
                        >
                          Quitar seleccion
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col surface-3">
      <div className="flex shrink-0 items-center justify-between hair-b px-4 py-3 surface-3">
        <button
          onClick={() => {
            setActiveRoute('select');
            setPlantas([]);
            setDetectError('');
            setArtifacts(null);
            setDetectedAreaHa(null);
            setGeotiffLayer(null);
            selectedRasterFileRef.current = null;
            setHasRasterFile(false);
            setGeometryValidated(false);
            setPreviewTab('map');
            setDetectMode('rgb');
            setPendingMsFiles({});
            setMsHint('');
            setBatchResults([]);
            setBatchReportUrl('');
            setBatchGeojsonUrl('');
          }}
          className="flex items-center gap-2 text-sm ink-2 hover:ink-2"
        >
          <ArrowLeft size={16} />
          Atras
        </button>
        <h2 className="text-sm font-semibold ink">
          {selectedParcela?.codigo || '-'} · {selectedParcela?.nombre || 'Lote'}
        </h2>
        <button
          onClick={handleSave}
          disabled={saving || plantas.length === 0}
          className="px-3 py-1.5 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-600 disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      <div className="px-4 py-2 hair-b bg-slate-900/70">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Lote</p>
            <p className="ink font-semibold truncate">{selectedParcela?.nombre || '-'}</p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Area</p>
            <p className="ink font-semibold">{formatHa(selectedParcelaStats.areaTotal)}</p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Bloques</p>
            <p className="ink font-semibold">{selectedParcelaStats.bloques}</p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Siembra</p>
            <p className="text-emerald-300 font-semibold">{formatHa(selectedParcelaStats.areaSembrada)}</p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">% Siembra</p>
            <p className="text-emerald-300 font-semibold">
              {selectedParcelaStats.areaTotal > 0
                ? `${((selectedParcelaStats.areaSembrada / selectedParcelaStats.areaTotal) * 100).toFixed(1)}%`
                : '-'}
            </p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Plantas</p>
            <p className="ink font-semibold">{displayedPlantCount > 0 ? formatNumber(displayedPlantCount) : '-'}</p>
          </div>
          <div className="rounded border hair bg-slate-900/40 px-2 py-1.5">
            <p className="ink-3">Densidad</p>
            <p className="ink font-semibold">
              {displayedPlantCount > 0 && displayedAreaHa > 0
                ? `${displayedDensity.toFixed(0)} pl/ha`
                : '-'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 gap-4 p-4">
        <div className="flex-1 rounded-lg border hair overflow-hidden flex flex-col">
          <div className="px-3 py-2 hair-b surface-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewTab('image')}
              className="px-3 py-1 text-xs rounded border transition"
              style={{
                borderColor: previewTab === 'image' ? '#16a34a' : '#334155',
                background: previewTab === 'image' ? 'rgba(22,163,74,.2)' : 'transparent',
                color: previewTab === 'image' ? '#86efac' : '#cbd5e1',
              }}
            >
              Imagen cargada
            </button>
            <button
              type="button"
              onClick={() => setPreviewTab('map')}
              className="px-3 py-1 text-xs rounded border transition"
              style={{
                borderColor: previewTab === 'map' ? '#16a34a' : '#334155',
                background: previewTab === 'map' ? 'rgba(22,163,74,.2)' : 'transparent',
                color: previewTab === 'map' ? '#86efac' : '#cbd5e1',
              }}
            >
              Mapa
            </button>
          </div>

          <div className="flex-1 min-h-0 bg-black overflow-auto">
            {geotiffLayer && previewTab === 'image' && geotiffLayer.origW && geotiffLayer.origH && (
              <div className="relative min-w-max">
                <img
                  src={geotiffLayer.url}
                  alt="raster cargado"
                  style={{ width: geotiffLayer.origW, height: geotiffLayer.origH, objectFit: 'fill', maxWidth: 'none' }}
                />
                {plantas.map((p, idx) =>
                  typeof p.x === 'number' && typeof p.y === 'number' ? (
                    <span
                      key={`${idx}-${p.x}-${p.y}`}
                      className="absolute rounded-full border border-white/80 bg-emerald-400/80"
                      style={{
                        width: 6,
                        height: 6,
                        left: p.x - 3,
                        top: p.y - 3,
                        boxShadow: '0 0 0 1px rgba(16,185,129,.35)',
                      }}
                    />
                  ) : null
                )}
              </div>
            )}

            {selectedParcela && previewTab === 'map' && (
              <GeoMapPreview
                geojson={selectedParcela.geojson || selectedBloque?.geojson}
                height="100%"
                maxWidthClassName="max-w-full h-full"
                polygonColor="#16a34a"
                rasterOverlay={
                  geotiffLayer?.georeferenced && geotiffLayer?.bounds
                    ? { url: geotiffLayer.url, bounds: geotiffLayer.bounds, opacity: 0.75 }
                    : null
                }
                overlays={[
                  ...(selectedBatchBloques.length > 0
                    ? selectedBatchBloques.map((b) => ({
                        geojson: b.geojson,
                        color: b.id === selectedBloqueId ? '#22c55e' : '#60a5fa',
                        flashing: false,
                      }))
                    : selectedBloque?.geojson
                      ? [
                          {
                            geojson: selectedBloque.geojson,
                            color: '#16a34a',
                            flashing: false,
                          },
                        ]
                      : []),
                ]}
              />
            )}
          </div>
        </div>

        <div className="w-80 flex flex-col gap-4">
          <div className="rounded-lg surface border hair p-3 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide ink-3">Flujo de ejecución</p>
              <p className="text-sm font-semibold ink mt-1">
                {selectedParcela?.codigo || '-'} · {selectedParcela?.nombre || 'Lote'}
              </p>
            </div>

            <div className="rounded border hair bg-slate-900/40 p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] uppercase tracking-wide ink-2">1. Bloques</p>
                <span className="text-[11px] text-emerald-300">Marcados: {selectedBatchBloqueIds.length}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 rounded border hair ink-2"
                  onClick={() => setSelectedBatchBloqueIds(bloques.map((b) => b.id))}
                >
                  Seleccionar todos
                </button>
                <button
                  type="button"
                  className="text-[11px] px-2 py-1 rounded border hair ink-2"
                  onClick={() => setSelectedBatchBloqueIds([])}
                >
                  Limpiar
                </button>
              </div>
              <div className="mt-2 max-h-28 overflow-y-auto space-y-1 pr-1">
                {bloques.map((b) => (
                  <label key={b.id} className="flex items-center justify-between gap-2 text-xs rounded border hair px-2 py-1">
                    <span className="ink-2">B{b.num}</span>
                    <span className="ink-2">{typeof b.area_ha === 'number' ? `${b.area_ha.toFixed(2)} ha` : '-'}</span>
                    <input
                      type="checkbox"
                      checked={selectedBatchBloqueIds.includes(b.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedBatchBloqueIds((prev) =>
                          checked ? (prev.includes(b.id) ? prev : [...prev, b.id]) : prev.filter((id) => id !== b.id)
                        );
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded border hair bg-slate-900/40 p-2 space-y-2">
              <p className="text-[11px] uppercase tracking-wide ink-2">2. Subir RGB/GeoTIFF</p>
              <button
                type="button"
                onClick={() => geotiffInputDetectRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-blue-600 bg-blue-900/20 text-blue-300 hover:bg-blue-900/40 text-sm font-medium transition"
              >
                <Upload size={14} />
                Subir RGB/GeoTIFF
              </button>
              <input
                ref={geotiffInputDetectRef}
                type="file"
                accept=".tif,.tiff,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const input = e.target as HTMLInputElement;
                  const file = input.files?.[0];
                  if (file) {
                    const name = file.name.toLowerCase();
                    if (name.endsWith('.tif') || name.endsWith('.tiff')) await processGeoTiff(file);
                    else await processRasterImage(file);
                  }
                  input.value = '';
                }}
              />
              {geotiffLayer?.sourceLabel && (
                <p className="text-[11px] ink-2 truncate">{geotiffLayer.sourceLabel}</p>
              )}
            </div>

            <div className="rounded border hair bg-slate-900/40 p-2 space-y-2">
              <p className="text-[11px] uppercase tracking-wide ink-2">3. Validar y ejecutar</p>
              {geotiffLayer?.georeferenced && (
                <button
                  type="button"
                  onClick={() => setGeometryValidated(true)}
                  className={`w-full py-2 rounded-lg text-sm font-semibold ${
                    geometryValidated
                      ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700'
                      : 'surface-2 ink hover-surface'
                  }`}
                >
                  {geometryValidated ? 'Geometría confirmada' : 'Confirmar geometría en mapa'}
                </button>
              )}
              <button
                type="button"
                onClick={handleDetectBatch}
                disabled={
                  batchDetecting ||
                  selectedBatchBloqueIds.length === 0 ||
                  !hasRasterFile ||
                  (!!geotiffLayer?.georeferenced && !geometryValidated)
                }
                className="w-full py-2.5 bg-emerald-700 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 text-sm font-semibold"
              >
                {batchDetecting
                  ? 'EJECUTANDO...'
                  : `EJECUTAR (${selectedBatchBloqueIds.length})`}
              </button>
              {!hasRasterFile && <p className="text-[11px] text-amber-300">Sube RGB/GeoTIFF para habilitar.</p>}
              {hasRasterFile && geotiffLayer?.georeferenced && !geometryValidated && (
                <p className="text-[11px] text-amber-300">Valida alineación en mapa y confirma geometría.</p>
              )}
              {selectedBatchBloqueIds.length === 0 && <p className="text-[11px] text-amber-300">Selecciona bloques.</p>}
            </div>
          </div>

          <div className="rounded-lg surface border hair p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide ink-3 mb-1">Resultado</p>
              <p className="text-[11px] uppercase tracking-wide text-emerald-400 mb-2">
                Modo {geotiffLayer?.mode === 'ms' ? 'MS-NDRE' : 'RGB'}
              </p>
              <p className="text-3xl font-bold text-green-400">
                {formatNumber(displayedPlantCount)}
              </p>
              <p className="text-xs ink-2 mt-1">plantas detectadas</p>
              {batchSummary.totalRows > 0 && (
                <p className="text-[11px] ink-2 mt-1">
                  Batch: {batchSummary.okCount}/{batchSummary.totalRows} bloques OK
                </p>
              )}
            </div>

            {displayedAreaHa > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide ink-3 mb-1">Densidad</p>
                <p className="text-lg font-semibold ink-2">
                  {displayedDensity.toFixed(0)}
                </p>
                <p className="text-xs ink-2">plantas/ha</p>
                {displayedAreaHa > 0 && (
                  <p className="text-[11px] ink-3 mt-1">Área detectada: {displayedAreaHa.toFixed(2)} ha</p>
                )}
              </div>
            )}

            {saveMessage && (
              <p className="text-xs font-semibold text-green-400">{saveMessage}</p>
            )}

            {detectError && (
              <p className="text-xs font-semibold text-red-400">{detectError}</p>
            )}

            {(batchReportUrl || artifacts?.reportUrl) && (
              <a
                href={batchReportUrl || artifacts?.reportUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-xs font-semibold text-emerald-300 underline"
              >
                Abrir reporte generado por Python
              </a>
            )}
            {(batchGeojsonUrl || artifacts?.jsonUrl) && (
              <a
                href={batchGeojsonUrl || artifacts?.jsonUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-xs font-semibold text-sky-300 underline"
              >
                Abrir JSON de resultado
              </a>
            )}
          </div>

          {batchResults.length > 0 && (
            <div className="rounded-lg surface border hair p-3 space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs uppercase tracking-wide ink-3">Resumen bloques marcados</p>
              {(batchReportUrl || batchGeojsonUrl) && (
                <div className="flex gap-3 pb-1">
                  {batchReportUrl && (
                    <a href={batchReportUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 underline font-semibold">
                      Abrir reporte consolidado
                    </a>
                  )}
                  {batchGeojsonUrl && (
                    <a href={batchGeojsonUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-300 underline font-semibold">
                      Abrir GeoJSON consolidado
                    </a>
                  )}
                </div>
              )}
              {batchReportUrl && (
                <a href={batchReportUrl} target="_blank" rel="noreferrer">
                  <img src={batchReportUrl} alt="reporte consolidado" className="w-full rounded border hair" />
                </a>
              )}
              {batchResults.map((row) => (
                <div key={row.bloqueId} className="rounded border hair p-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold ink-2">B{row.bloqueNum}</span>
                    {row.error ? (
                      <span className="text-red-400">Error</span>
                    ) : (
                      <span className="text-emerald-400">{formatNumber(row.conteo)} plantas</span>
                    )}
                  </div>
                  {!row.error && (
                    <p className="text-[11px] ink-2 mt-1">
                      {row.areaHa.toFixed(2)} ha · {row.densidad.toFixed(0)} pl/ha · KPI {row.kpi.toFixed(1)}%
                    </p>
                  )}
                  {row.error && <p className="text-[11px] text-red-400 mt-1">{row.error}</p>}
                  <div className="flex gap-3 mt-1">
                    {row.reportUrl && (
                      <a href={row.reportUrl} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-300 underline">
                        Reporte
                      </a>
                    )}
                    {row.jsonUrl && (
                      <a href={row.jsonUrl} target="_blank" rel="noreferrer" className="text-[11px] text-sky-300 underline">
                        GeoJSON
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConteoPlantasV2Page;











